import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { CountryIso3166Schema } from "@tfg/types/schemas/enum"

import { FormElement } from "~/pages/Admin/components/TypesForm.tsx"

export const useFields: (isUpdate: boolean) => FormElement[] = (isUpdate) => {
  const { t } = useTranslation(["traceability"])
  const fields = useMemo(
    (): FormElement[] => [
      {
        type: "switch",
        name: "organization.businessRelationship.isActive",
        label: t("contactModal.labels.companyIsActive"),
        tooltip: t("contactModal.labels.companyIsActiveTooltip"),
        required: true,
        placeholder: t("contactModal.labels.companyIsActivePlaceholder"),
        defaultChecked: true,
        tooltipJustify: "start",
      },
      {
        type: "text",
        name: "organization.name",
        label: t("contactModal.labels.companyName"),
        placeholder: t("contactModal.labels.companyNamePlaceholder"),
        tooltip: t("contactModal.labels.companyNameTooltip"),
        required: true,
        disabled: isUpdate,
      },
      {
        type: "select",
        clearable: false,
        name: "organization.businessRelationship.type",
        label: t("contactModal.labels.companyRelationshipTypeName"),
        placeholder: "",
        tooltip: t("contactModal.labels.companyRelationshipTypeTooltip"),
        required: true,
        span: 6,
        options: [
          {
            label: t("contactModal.labels.companyRelationshipType.AGENT"),
            value: "AGENT",
          },
          {
            label: t(
              "contactModal.labels.companyRelationshipType.MANUFACTURER"
            ),
            value: "MANUFACTURER",
          },
          {
            label: t("contactModal.labels.companyRelationshipType.SUPPLIER"),
            value: "SUPPLIER",
          },
          {
            label: t(
              "contactModal.labels.companyRelationshipType.RAW_MATERIAL_SUPPLIER"
            ),
            value: "RAW_MATERIAL_SUPPLIER",
          },
          {
            label: t("contactModal.labels.companyRelationshipType.TRADER"),
            value: "TRADER",
          },
          {
            label: t("contactModal.labels.companyRelationshipType.BRAND"),
            value: "BRAND",
          },
          {
            label: t("contactModal.labels.companyRelationshipType.OTHER"),
            value: "OTHER",
          },
        ],
      },
      {
        type: "text",
        name: "organization.businessRelationship.supplierCode",
        label: t("contactModal.labels.companySupplierCode"),
        placeholder: t("contactModal.labels.companySupplierCodePlaceholder"),
        tooltip: t("contactModal.labels.companySupplierCodeTooltip"),
        required: false,
        span: 6,
      },
      {
        type: "selectListRegistrationId",
        name: "organization.registrationId",
        label: t("contactModal.labels.companyRegistrationId"),
        placeholder: t("contactModal.labels.companyRegistrationIdPlaceholder"),
        tooltip: t("contactModal.labels.companyRegistrationIdTooltip"),
        required: false,
        options: [
          {
            label: "VAT",
            value: "VAT",
            description: t(
              "contactModal.labels.companyRegistrationIdDescription.VAT"
            ),
          },
          {
            label: "EORI",
            value: "EORI",
            description: t(
              "contactModal.labels.companyRegistrationIdDescription.EORI"
            ),
          },
          {
            label: "GLN",
            value: "GLN",
            description: t(
              "contactModal.labels.companyRegistrationIdDescription.GLN"
            ),
          },
          {
            label: "DUNS",
            value: "DUNS",
            description: t(
              "contactModal.labels.companyRegistrationIdDescription.DUNS"
            ),
          },
          {
            label: "RCS",
            value: "RCS",
            description: t(
              "contactModal.labels.companyRegistrationIdDescription.RCS"
            ),
          },
          {
            label: "OTHER",
            value: "OTHER",
            description: t(
              "contactModal.labels.companyRegistrationIdDescription.OTHER"
            ),
          },
        ].sort((a, b) => a.label.localeCompare(b.label)),
      },
      {
        type: "text",
        name: "organization.street",
        label: t("contactModal.labels.companyStreet"),
        placeholder: t("contactModal.labels.companyStreetPlaceholder"),
        tooltip: t("contactModal.labels.companyStreetTooltip"),
        required: true,
        span: 6,
      },
      {
        type: "text",
        span: 6,
        name: "organization.city",
        label: t("contactModal.labels.companyCity"),
        placeholder: t("contactModal.labels.companyCityPlaceholder"),
        tooltip: t("contactModal.labels.companyCityTooltip"),
        required: true,
      },
      {
        type: "text",
        name: "organization.postalCode",
        label: t("contactModal.labels.companyPostalCode"),
        placeholder: t("contactModal.labels.companyPostalCodePlaceholder"),
        tooltip: t("contactModal.labels.companyPostalCodeTooltip"),
        required: true,
        span: 6,
      },
      {
        type: "countrySelect",
        name: "organization.country",
        label: t("contactModal.labels.companyCountry"),
        placeholder: CountryIso3166Schema.enum.FR,
        tooltip: t("contactModal.labels.companyCountryTooltip"),
        required: true,
        span: 6,
      },

      {
        type: "group",
        title: t("contactModal.labels.contactInformation"),
        fields: [
          {
            type: "row",
            fields: [
              {
                type: "text",
                name: "name",
                label: t("contactModal.labels.name"),
                placeholder: t("contactModal.labels.namePlaceholder"),
                tooltip: t("contactModal.labels.nameTooltip"),
                required: true,
                span: 6,
              },
              {
                type: "text",
                span: 6,
                name: "email",
                label: t("contactModal.labels.email"),
                placeholder: t("contactModal.labels.emailPlaceholder"),
                tooltip: t("contactModal.labels.emailTooltip"),
                required: true,
                disabled: isUpdate,
              },
              {
                type: "languageSelect",
                span: 6,
                name: "preferedLangageIso6391",
                label: t("contactModal.labels.preferredLanguage"),
                placeholder: "",
                tooltip: "",
                required: true,
              },
            ],
          },
        ],
      },
    ],
    [t, isUpdate]
  )
  return fields
}
